import { defineStore } from 'pinia';
import { createRouter, createMemoryHistory } from "vue-router";
import { asyncRoutes } from '@/Router';
import { setPagestate } from '@/Directive/AutoSavePageState/AutoSavePageStateFun';
import {
  apiGetProjectApps,
  apiGetProjects,
  apiGetProjectTags,
  apiGetTesterProjectApps,
  apiGetTestGroups,
  apiGetTestUsers
} from '@/Api/AppApi';
import { ElMessage } from 'element-plus';
import { MaxPageCount } from '@/settings';
import { useTechStore } from './TechStore';

const ProjectTypeEnum = {
  admin: 1,
  tester: 2,
  1: "admin",
  2: "tester"
};

const UserTypeEnum = {
  0: "superAdmin",
  1: "user",
  user: 0,
  superAdmin: 1
};

export const useProjectStore = defineStore('project', {
  state: () => ({
    projectList: [],
    projectMap: null,
    currentProjectId: "",
    // 项目相关
    routes: [],
    loadingApps: false,
    projectApps: [],
    projectTags: [],
    testUsers: [],
    testGroups: [],
    currentAction:"",
    currentAppID:""
  }),
  getters: {
    isTester(state) {
      if (state.projectMap === null) {
        return false;
      }
      const project = state.projectMap[state.currentProjectId];
      if (!project) {
        return false;
      }

      return project.type === ProjectTypeEnum.tester;
    },
    isAdmin(state) {
      if (state.projectMap === null) {
        return false;
      }
      const project = state.projectMap[state.currentProjectId];
      if (!project) {
        return false;
      }

      return project.type === ProjectTypeEnum.admin;
    },
    isSuperAdmin() {
      const techStore = useTechStore();
      return techStore.userInfo?.type == UserTypeEnum.superAdmin;
    }
  },
  actions: {
    checkRoute() {
      const vRouter = createRouter({
        history: createMemoryHistory(),
        routes: this.routes,
      });
      const route = this.$router.currentRoute.value;
      if (!vRouter.hasRoute(route.name)) {
        // 当前路径无权限则转跳
        let to = '404';
        const walk = (list) => {
          for (const item of list) {
            if (item.name) {
              to = item.name;
              return true;
            }
            if (item.children && item.children.length > 0) {
              if (walk(item.children)) {
                return true;
              }
            }
          }
        };
        walk(this.routes); // 转跳到第一个有权限的路径
        this.$router.push({ name: to }); // 若此前有转跳，会被覆盖
      }
    },
    changeCurrentProject(id) {
      if(id === undefined){
        this.currentProjectId = "";
        this.$router.push({ name: 'noAuth' }); // 若此前有转跳，会被覆盖
      }else{
        if (this.currentProjectId === id) {
          return;
        }
        this.currentProjectId = id;
        if (!window.__POWERED_BY_QIANKUN__) {
          setPagestate("selectproject", id);
          const route = this.$router.currentRoute.value;
          const to = {
            path: route.path,
            params: route.params,
            query: {
              ...route.query,
              project: id,
              appid: route.query.project === id ? route.query.appid : undefined
            },
            hash: route.hash,
          };
          this.$router.replace(to);
          // 获取新的路由
          const accessRoutes = [];
          const filter = (item) => {
            if (!item.meta) {
              return true;
            }

            if (item.meta.justSuperAdmin) {
              if (this.isSuperAdmin) {
                return !item.meta.justAdmin || this.isAdmin;
              } else {
                return false;
              }
            }
            if (item.meta.justAdmin) {
              return this.isAdmin;
            }
            if (item.meta.justTester) {
              return this.isTester;
            }
            return true;
          };
          const dfs = (from, to) => {
            for (const item of from) {
              if (filter(item)) {
                const clone = { ...item, children: undefined };
                if (item.children && item.children.length > 0) {
                  clone.children = [];
                  dfs(item.children, clone.children);
                  if (clone.children.length === 0) {
                    clone.children = undefined;
                  }
                }
                to.push(clone);
              }
            }
          };
          dfs(asyncRoutes, accessRoutes);
          // 触发路由更新
          this.routes = accessRoutes;
          this.checkRoute();
          this.clearRelated();
          this.updateRelated();
        }else{
          const projectList = this.projectList;
          if (projectList.length === 0) {
            apiGetProjects().then((res) => {
              if (res.code === 0) {
                const data = res.data;
                if (data.length === 0) {
                  ElMessage.error("你似乎没有权限哦~");
                } else {
                  this.updateProjectList(data);
                }
                this.clearRelated();
                this.updateRelated();
                if(this.$router.currentRoute.value.name === 'noAuth'){
                  this.$router.push({ name: 'Overview' }); // 若此前有转跳，会被覆盖
                }
              }
            });
          }else{
            if(this.$router.currentRoute.value.name === 'noAuth'){
              this.$router.push({ name: 'Overview' }); // 若此前有转跳，会被覆盖
            }
          }
        }
      }

    },
    updateProjectList(list) {
      this.projectList = list;
      this.projectMap = {};
      for (const project of list) {
        this.projectMap[project.id] = project;
      }
    },

    clearRelated() {
      // 清空project相关数据
      this.projectTags = [];
      this.projectApps = [];
      this.testUsers = [];
      this.testGroups = [];
    },
    updateRelated() {
      // project相关数据更新
      if (this.isAdmin) {
        this.getProjectApps().catch((e) => { ElMessage.error(e.message); });
        this.getProjectTags().catch((e) => { ElMessage.error(e.message); });
        this.getTestGroups().catch((e) => { ElMessage.error(e.message); });
        this.getTestUsers().catch((e) => { ElMessage.error(e.message); });
      } else if (this.isTester) {
        this.getTesterProjectApps().catch((e) => { ElMessage.error(e.message); });
      }
    },
    async getProjectApps() {
      this.loadingApps = true;
      const res = await apiGetProjectApps(this.currentProjectId);
      this.loadingApps = false;
      if (res.code === 0) {
        this.projectApps = res.data || [];

        this.projectApps.forEach((app) => {
          if (app.tags === undefined) {
            // 如果后端没有返回 tags，则会导致 vue 的响应式无法处理 tags 的更新, 这里补上这个字段
            app.tags = [];
          }
        });

        this.projectApps.sort((a, b) => b.priority - a.priority);

        return this.projectApps;
      } else if (res.code === 10004) {
        ElMessage.error("你似乎没有权限哦~");
        // this.$router.push('/home');
      } else {
        throw new Error(res.message);
      }
    },
    async getProjectTags() {
      const res = await apiGetProjectTags(this.currentProjectId);
      if (res.code === 0) {
        this.projectTags = res.data;
        return this.projectTags;
      } else if (res.code === 10004) {
        ElMessage.error("你似乎没有权限哦~");
        // this.$router.push('/home');
      } else {
        throw new Error(res.message);
      }
    },
    async getTesterProjectApps() {
      this.loadingApps = true;
      const res = await apiGetTesterProjectApps(0, MaxPageCount, "");
      this.loadingApps = false;
      if (res.code === 0) {
        this.projectApps = (res.data || []).filter((app) => {
          return this.currentProjectId === app.projectid;
        });
        return this.projectApps;
      } else if (res.code === 10004) {
        ElMessage.error("你似乎没有权限哦~");
        // this.$router.push('/home');
      } else {
        throw new Error(res.message);
      }
    },
    async getTestGroups() {
      const res = await apiGetTestGroups(this.currentProjectId);
      if (res.code === 0) {
        this.testGroups = res.data.data || [];
        this.testGroups.forEach(function (g) {
          if (!g.members) {
            g.members = [];
          }
          if (!g.apps) {
            g.apps = [];
          }
        });
        return this.testGroups;
      } else if (res.code === 10004) {
        ElMessage.error("你似乎没有权限哦~");
        // this.$router.push('/home');
      } else {
        throw new Error(res.message);
      }
    },
    async getTestUsers() {
      const res = await apiGetTestUsers(this.currentProjectId);
      if (res.code === 0) {
        this.testUsers = res.data.data || [];
        this.testUsers.forEach(function (u) {
          if (!u.groups) {
            u.groups = [];
          }
          if (!u.apps) {
            u.apps = [];
          }
        });
        return this.testUsers;
      } else if (res.code === 10004) {
        ElMessage.error("你似乎没有权限哦~");
        // this.$router.push('/home');
      } else {
        throw new Error(res.message);
      }
    }
  }
});
